/**
 * Gestion du consentement cookies RGPD
 * Stocke les préférences de l'utilisateur dans localStorage
 */

export interface CookieConsent {
  necessary: boolean; // Toujours true (cookies essentiels)
  analytics: boolean; // Google Analytics
  marketing: boolean; // Réservé pour futur usage
  timestamp: number;
}

const CONSENT_KEY = 'aspexit-cookie-consent';
const CONSENT_VERSION = '1.0';

/**
 * Récupérer le consentement actuel
 */
export function getConsent(): CookieConsent | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);

    // Vérifier la version du consentement
    if (data.version !== CONSENT_VERSION) {
      return null;
    }

    return data.consent;
  } catch {
    return null;
  }
}

/**
 * Sauvegarder le consentement
 */
export function saveConsent(consent: Omit<CookieConsent, 'necessary' | 'timestamp'>): void {
  const fullConsent: CookieConsent = {
    necessary: true, // Toujours vrai
    ...consent,
    timestamp: Date.now(),
  };

  localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify({
      version: CONSENT_VERSION,
      consent: fullConsent,
    })
  );

  // Émettre un événement custom pour notifier les autres composants
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: fullConsent }));
}

/**
 * Vérifier si l'utilisateur a donné son consentement
 */
export function hasConsent(): boolean {
  return getConsent() !== null;
}

/**
 * Vérifier si l'utilisateur a accepté les cookies analytics
 */
export function hasAnalyticsConsent(): boolean {
  const consent = getConsent();
  return consent?.analytics === true;
}

/**
 * Accepter tous les cookies
 */
export function acceptAll(): void {
  saveConsent({
    analytics: true,
    marketing: false, // Pas encore utilisé
  });
}

/**
 * Refuser tous les cookies non-essentiels
 */
export function rejectAll(): void {
  saveConsent({
    analytics: false,
    marketing: false,
  });
}

/**
 * Réinitialiser le consentement (afficher à nouveau le banner)
 */
export function resetConsent(): void {
  localStorage.removeItem(CONSENT_KEY);
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: null }));
}
